import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { fmt } from '@/lib/api';

const BW_LABELS = { contant: 'Contant', bancontact: 'Bancontact', visa: 'Visa', maestro: 'Maestro' };

export default function KlantDisplayPage() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [tick, setTick] = useState(0);
  const [bannerIdx, setBannerIdx] = useState(0);
  const qrRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const API = process.env.REACT_APP_BACKEND_URL;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/api/public/kassa-display/${code}`);
        if (alive) { setData(r.data); setErr(''); }
      } catch (e) {
        if (alive) setErr(e.response?.status === 404 ? 'Display-code niet gekoppeld' : 'Verbindingsfout');
      }
    };
    poll();
    const iv = setInterval(poll, 1500);
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => { alive = false; clearInterval(iv); clearInterval(t); };
  }, [code]);

  const state = data?.state || {};
  const bedrijf = data?.bedrijf || {};
  const disp = data?.display || {};
  const lijnen = state.lijnen || [];
  const isPaying = state.status === 'paying';
  const isDone = state.status === 'done';
  const isIdle = !state.status || state.status === 'idle';
  const API = process.env.REACT_APP_BACKEND_URL;

  const banners = disp.banners || [];
  const bannerInterval = Math.max(2, disp.banner_interval || 6);
  const bg1 = disp.bg1 || '#002FA7';
  const bg2 = disp.bg2 || '#001A5C';
  const accent = disp.accent || '#FEE68A';

  // Roteer banners in idle-modus
  useEffect(() => {
    if (!isIdle || banners.length < 2) return;
    const iv = setInterval(() => setBannerIdx(i => (i + 1) % banners.length), bannerInterval * 1000);
    return () => clearInterval(iv);
  }, [isIdle, banners.length, bannerInterval]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#000', color: '#fff' }}>
        <div className="text-center">
          <div className="text-5xl mb-4">⚠</div>
          <div className="text-2xl font-bold mb-2">{err}</div>
          <div className="text-gray-400 font-mono text-lg">Code: {code}</div>
          <div className="text-gray-500 text-sm mt-6">Vraag de kassier om het display te herstarten in Kassa → Klant-display.</div>
        </div>
      </div>
    );
  }

  const bgStyle = { background: `linear-gradient(135deg,${bg1} 0%,${bg2} 100%)` };

  return (
    <div className="min-h-screen" style={{ ...bgStyle, color: '#fff', fontFamily: 'ui-sans-serif,system-ui' }}>
      {/* Header */}
      <div className="px-8 py-5 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
        <div className="flex items-center gap-4">
          {disp.toon_logo !== false && (bedrijf.logo ? (
            <img src={bedrijf.logo} alt="logo" className="h-12 w-auto object-contain" style={{ maxWidth: 140, background: '#fff', padding: 4, borderRadius: 8 }} />
          ) : (
            <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-2xl" style={{ background: '#fff', color: bg1 }}>
              {(bedrijf.naam || 'K')[0]}
            </div>
          ))}
          <div>
            <div className="text-2xl font-bold">{bedrijf.naam || 'Kassa'}</div>
            {bedrijf.slogan && <div className="text-sm opacity-70">{bedrijf.slogan}</div>}
          </div>
        </div>
        {disp.toon_klok !== false && (
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider opacity-60">Klant-display</div>
            <div className="font-mono text-lg">{new Date().toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</div>
          </div>
        )}
      </div>

      {!data && (
        <div className="flex items-center justify-center min-h-[60vh] opacity-70 text-xl">Verbinden…</div>
      )}

      {data && isIdle && banners.length > 0 && (
        <div className="relative" style={{ height: 'calc(100vh - 100px)' }}>
          {banners.map((b, i) => (
            <img
              key={i}
              src={b}
              alt={`Banner ${i + 1}`}
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
              style={{ opacity: i === bannerIdx ? 1 : 0 }}
            />
          ))}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 px-6 py-3 rounded-full text-center" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
            <div className="text-lg font-bold">{bedrijf.welkom || 'Welkom!'}</div>
          </div>
          {banners.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {banners.map((_, i) => (
                <span key={i} className="w-2 h-2 rounded-full transition" style={{ background: i === bannerIdx ? '#fff' : 'rgba(255,255,255,0.35)' }} />
              ))}
            </div>
          )}
        </div>
      )}

      {data && isIdle && banners.length === 0 && (
        <div className="flex items-center justify-center min-h-[70vh] text-center px-8">
          <div>
            <div className="text-6xl mb-6">🛒</div>
            <div className="text-4xl font-bold mb-2">{bedrijf.welkom || 'Welkom!'}</div>
            <div className="text-lg opacity-70">Klaar om u te helpen</div>
          </div>
        </div>
      )}

      {data && (state.status === 'shopping' || isPaying) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-8 min-h-[80vh]">
          {/* Items */}
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-wider opacity-60 mb-3">Uw winkelwagen — {lijnen.length} artikel{lijnen.length === 1 ? '' : 'en'}</div>
            <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(8px)' }}>
              <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {lijnen.length === 0 && (
                  <div className="p-12 text-center opacity-60 text-lg">Geen artikelen</div>
                )}
                {lijnen.map((l, i) => (
                  <div key={i} className="flex items-center justify-between px-6 py-4 text-xl" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="flex-1 truncate">
                      <div className="flex items-center gap-3">
                        <span className="text-sm opacity-60 font-mono w-6 text-right">{l.aantal || 1}×</span>
                        <span>{l.omschr}</span>
                      </div>
                    </div>
                    <div className="font-mono font-bold ml-4">{fmt((l.prijs || 0) * (l.aantal || 1))}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Total + QR */}
          <div className="flex flex-col">
            <div className="rounded-xl p-6 mb-4" style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)' }}>
              <div className="text-xs uppercase tracking-wider opacity-60 mb-2">{isPaying ? 'Te betalen' : 'Totaal'}</div>
              <div className="text-6xl font-bold font-mono leading-none" style={{ color: isPaying ? accent : '#fff' }}>{fmt(state.totaal || 0)}</div>
            </div>

            {isPaying && (
              <div className="rounded-xl p-6 text-center" style={{ background: '#fff', color: '#111' }}>
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-bold">Scan om te betalen</div>
                <div className="flex items-center justify-center mb-2">
                  {bedrijf.iban ? (
                    <img
                      ref={qrRef}
                      src={`${API}/api/public/kassa-display/${code}/qr?bedrag=${(state.totaal || 0).toFixed(2)}&ref=Kassa&t=${tick}`}
                      alt="Betaal-QR"
                      className="w-48 h-48"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-48 h-48 flex items-center justify-center bg-gray-100 rounded text-gray-400 text-xs text-center px-4">IBAN niet ingesteld in app</div>
                  )}
                </div>
                <div className="text-xs text-gray-500">Of betaal met uw kaart aan de kassa</div>
              </div>
            )}

            {!isPaying && (
              <div className="rounded-xl p-4 text-center opacity-70 text-sm" style={{ background: 'rgba(255,255,255,0.06)' }}>
                Scan uw artikelen of vraag hulp aan de kassier
              </div>
            )}
          </div>
        </div>
      )}

      {isDone && (
        <div className="flex items-center justify-center min-h-[70vh] text-center px-8">
          <div>
            <div className="text-7xl mb-6" style={{ animation: 'pulse 1.5s infinite' }}>✓</div>
            <div className="text-5xl font-bold mb-3" style={{ color: '#86EFAC' }}>Betaald</div>
            <div className="text-3xl font-mono mb-4">{fmt(state.totaal || 0)}</div>
            {(state.wisselgeld || 0) > 0.01 && (
              <div className="text-2xl mb-3">Wisselgeld: <span className="font-bold font-mono">{fmt(state.wisselgeld)}</span></div>
            )}
            {state.ticket_id && <div className="text-sm opacity-60 font-mono mb-6">Ticket {state.ticket_id}</div>}
            <div className="text-xl opacity-80 mt-6">{bedrijf.bedank || 'Bedankt voor uw bezoek!'}</div>
          </div>
        </div>
      )}

      <div className="fixed bottom-3 right-4 text-[10px] opacity-30 font-mono">code · {code}</div>
    </div>
  );
}
